"use strict";
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePageNumbers = exports.ELLIPSIS = void 0;
var react_1 = require("react");
exports.ELLIPSIS = '...';
/**
 * Hook used to determine the range of page numbers to be displayed.
 * For large numbers of pages, the range will include ellipsis separators.
 * e.g.: [1, '...', 4, 5, 6, '...', 10]
 *
 * @param currentPage current page number
 * @param totalPages total number of pages
 * @param siblingCount number of page numbers to be displayed on each side of the current page
 * @returns an array containing the range of numbers to be displayed
 */
var usePageNumbers = function (currentPageParam, totalPagesParam, siblingCountParam) {
    if (siblingCountParam === void 0) { siblingCountParam = 1; }
    var getConsecutiveIntegers = function (start, end) {
        var length = end - start + 1;
        return Array.from({ length: length }, function (_, index) { return start + index; });
    };
    var pageNumbers = (0, react_1.useMemo)(function () {
        var currentPage = Math.max(currentPageParam, 1);
        var totalPages = Math.max(currentPage, totalPagesParam);
        var siblingCount = Math.max(siblingCountParam, 1);
        var firstPage = 1;
        var lastPage = totalPages;
        // To avoid dramatic changes in pager size, limit the number of items to be displayed.
        // Calculated as 1(first page) + 1(last page) + 1(current page) + 2(ellipses) + 2 * siblingCount.
        var maxNumberOfItems = 5 + 2 * siblingCount;
        // When the total number of pages less than the max number of items, no ellipsis will be displayed.
        // e.g.: [1, 2, 3, 4]
        if (totalPages < maxNumberOfItems) {
            return getConsecutiveIntegers(1, totalPages);
        }
        // Determine if ellipsis separators should be displayed on left or right side, or both.
        var leftSiblingPage = Math.max(currentPage - siblingCount, firstPage);
        var rightSiblingPage = Math.min(currentPage + siblingCount, lastPage);
        var shouldHaveStartEllipsis = leftSiblingPage > 2;
        var shouldHaveEndEllipsis = rightSiblingPage < lastPage - 1;
        // Left side ellipsis separator
        // e.g.: [1, '...', 6, 7, 8, 9, 10]
        if (shouldHaveStartEllipsis && !shouldHaveEndEllipsis) {
            // 1(current page) + 1 (last page) + 1(page instead end ellipsis) + 2 * siblingCount
            var rightItemCount = 3 + 2 * siblingCount;
            var rightItems = getConsecutiveIntegers(lastPage - rightItemCount + 1, lastPage);
            return __spreadArray([firstPage, exports.ELLIPSIS], rightItems, true);
        }
        // Right side ellipsis separator
        // e.g.: [1, 2, 3, 4, 5, '...', 10]
        if (!shouldHaveStartEllipsis && shouldHaveEndEllipsis) {
            // 1(current page) + 1 (last page) + 1(page instead start ellipsis) + 2 * siblingCount
            var leftItemCount = 3 + 2 * siblingCount;
            var leftItems = getConsecutiveIntegers(firstPage, leftItemCount);
            return __spreadArray(__spreadArray([], leftItems, true), [exports.ELLIPSIS, lastPage], false);
        }
        // Ellipsis separators on both sides
        // e.g.: [1, '...', 4, 5, 6, '...', 10]
        var middleItems = getConsecutiveIntegers(leftSiblingPage, rightSiblingPage);
        return __spreadArray(__spreadArray([firstPage, exports.ELLIPSIS], middleItems, true), [exports.ELLIPSIS, lastPage], false);
    }, [currentPageParam, totalPagesParam, siblingCountParam]);
    return pageNumbers;
};
exports.usePageNumbers = usePageNumbers;
